import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { toast } from "sonner";
import { formatDate, formatApiError } from "../lib/format";
import { Plus, Trash2, ClipboardList, Package, Calendar, ArrowLeftRight, ExternalLink } from "lucide-react";

// Schema-driven workspace panel — works for MUA, Decor, PBH, Snap, WO, Music, Attire, Handcraft.
export default function ServiceWorkspacePanel({ bookingId, schema, canEdit = true }) {
  const [ws, setWs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState({});
  const [notes, setNotes] = useState("");
  const [stage, setStage] = useState("");
  const [pct, setPct] = useState(0);
  const [itemDrafts, setItemDrafts] = useState({}); // key -> text
  const [ap, setAp] = useState({ label: "", at: "", location: "", pic: "", note: "" });
  const [ho, setHo] = useState({ kind: "out", condition: "", note: "" });

  const ensure = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/service-workspaces", {
        booking_id: bookingId, division_id: schema.division_id, kind: schema.kind,
      });
      setWs(data);
      setNotes(data.notes || "");
      setStage(data.progress?.stage || schema.stages[0] || "");
      setPct(data.progress?.pct || 0);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail || "Gagal memuat")); }
    finally { setLoading(false); }
  };
  useEffect(() => { ensure(); /* eslint-disable-next-line */ }, [bookingId, schema.kind]);

  const refresh = (data) => setWs(data);

  const briefValue = (k) => {
    if (dirty[k] !== undefined) return dirty[k];
    const v = ws?.brief?.[k];
    if (Array.isArray(v)) return v.join(" ");
    return v ?? "";
  };
  const setBrief = (k, v) => setDirty((x) => ({ ...x, [k]: v }));

  const saveBrief = async () => {
    if (!Object.keys(dirty).length) return;
    const brief = { ...(ws?.brief || {}) };
    for (const [k, v] of Object.entries(dirty)) {
      const f = schema.briefFields.find((x) => x.key === k);
      if (f?.isList) brief[k] = (v || "").split(/\s+/).filter(Boolean);
      else brief[k] = v;
    }
    try { const { data } = await api.put(`/service-workspaces/${ws.id}`, { brief });
      toast.success("Brief tersimpan"); refresh(data); setDirty({});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveProgress = async () => {
    try { const { data } = await api.put(`/service-workspaces/${ws.id}`,
      { notes, progress_stage: stage, progress_pct: Number(pct) || 0 });
      toast.success("Progres tersimpan"); refresh(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addItem = async (key) => {
    const text = (itemDrafts[key] || "").trim(); if (!text) return;
    try { const { data } = await api.post(`/service-workspaces/${ws.id}/checklists/${key}/items`, { text });
      refresh(data); setItemDrafts((d) => ({ ...d, [key]: "" }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleItem = async (key, iid, done) => {
    try { const { data } = await api.put(`/service-workspaces/${ws.id}/checklists/${key}/items/${iid}`, { done }); refresh(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delItem = async (key, iid) => {
    try { const { data } = await api.delete(`/service-workspaces/${ws.id}/checklists/${key}/items/${iid}`); refresh(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addHandover = async () => {
    try { const { data } = await api.post(`/service-workspaces/${ws.id}/handovers`, ho);
      toast.success("Serah terima dicatat"); refresh(data);
      setHo({ kind: "out", condition: "", note: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delHandover = async (id) => {
    try { const { data } = await api.delete(`/service-workspaces/${ws.id}/handovers/${id}`); refresh(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addAppointment = async () => {
    if (!ap.label.trim() || !ap.at.trim()) return toast.error("Isi label dan waktu");
    try { const { data } = await api.post(`/service-workspaces/${ws.id}/appointments`, ap);
      toast.success("Jadwal ditambahkan"); refresh(data);
      setAp({ label: "", at: "", location: "", pic: "", note: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleAppointment = async (aid, done) => {
    try { const { data } = await api.put(`/service-workspaces/${ws.id}/appointments/${aid}`, { done }); refresh(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delAppointment = async (aid) => {
    try { const { data } = await api.delete(`/service-workspaces/${ws.id}/appointments/${aid}`); refresh(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading || !ws) return <div className="text-sm text-[#8C9096]">Memuat ruang kerja {schema.label}…</div>;

  return (
    <div className="space-y-4" data-testid={`sw-${schema.kind}`}>
      {/* Header */}
      <Card className="p-4 border border-[#E5E0D8] bg-white">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-sm font-semibold">{ws.title}</div>
            <div className="text-[11px] text-[#8C9096]">Ruang kerja {schema.label}</div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={stage} onValueChange={setStage}>
              <SelectTrigger className="w-40 h-8 text-xs bg-white" data-testid={`sw-${schema.kind}-stage`}><SelectValue placeholder="Tahap"/></SelectTrigger>
              <SelectContent>{schema.stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="number" min="0" max="100" className="w-16 h-8 text-xs bg-white" value={pct} onChange={(e) => setPct(e.target.value)}/>
            <span className="text-[11px] text-[#8C9096]">%</span>
            {canEdit && <Button size="sm" onClick={saveProgress} className="bg-brand text-white" data-testid={`sw-${schema.kind}-save-progress`}>Simpan</Button>}
          </div>
        </div>
        <div className="w-full h-1.5 bg-[#F0EBE0] rounded mt-2 overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }}/>
        </div>
      </Card>

      {/* Brief */}
      <Card className="p-4 border border-[#E5E0D8] bg-white space-y-3">
        <div className="text-sm font-semibold">Brief</div>
        <div className="grid grid-cols-2 gap-3">
          {schema.briefFields.map((f) => (
            <div key={f.key} className={f.textarea ? "col-span-2" : ""}>
              <Label className="text-xs">{f.label}</Label>
              {f.textarea
                ? <Textarea rows={2} className="mt-1 text-sm" placeholder={f.placeholder || ""} value={briefValue(f.key)} onChange={(e) => setBrief(f.key, e.target.value)} data-testid={`sw-${schema.kind}-brief-${f.key}`}/>
                : <Input className="mt-1 text-sm" placeholder={f.placeholder || ""} value={briefValue(f.key)} onChange={(e) => setBrief(f.key, e.target.value)} data-testid={`sw-${schema.kind}-brief-${f.key}`}/>}
            </div>
          ))}
        </div>
        {canEdit && <div className="flex justify-end">
          <Button size="sm" onClick={saveBrief} disabled={!Object.keys(dirty).length} className="bg-brand text-white" data-testid={`sw-${schema.kind}-save-brief`}>Simpan Brief</Button>
        </div>}
      </Card>

      {/* Notes */}
      <Card className="p-4 border border-[#E5E0D8] bg-white space-y-2">
        <div className="text-sm font-semibold">Catatan Internal</div>
        <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="text-sm"/>
      </Card>

      {/* Checklists — multiple */}
      {schema.checklistKeys.map((cl) => {
        const found = (ws.checklists || []).find((x) => x.key === cl.key);
        const items = found?.items || [];
        const done = items.filter((x) => x.done).length;
        return (
          <Card key={cl.key} className="p-4 border border-[#E5E0D8] bg-white space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold flex items-center gap-1"><ClipboardList className="w-4 h-4"/>{cl.label}</div>
              <div className="text-[11px] text-[#8C9096]">{done}/{items.length}</div>
            </div>
            <div className="space-y-1.5">
              {items.map((it) => (
                <div key={it.id} className="flex items-start gap-2 group" data-testid={`sw-${schema.kind}-${cl.key}-${it.id}`}>
                  <Checkbox checked={!!it.done} disabled={!canEdit} onCheckedChange={(v) => toggleItem(cl.key, it.id, !!v)}/>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm ${it.done ? "line-through text-[#8C9096]" : ""}`}>{it.text}</div>
                    {it.done && <div className="text-[10px] text-[#8C9096]">✓ {it.done_by} • {formatDate(it.done_at)}</div>}
                  </div>
                  {canEdit && <button onClick={() => delItem(cl.key, it.id)} className="opacity-0 group-hover:opacity-100 text-rose-500"><Trash2 className="w-3.5 h-3.5"/></button>}
                </div>
              ))}
              {items.length === 0 && <div className="text-xs italic text-[#8C9096]">Belum ada item.</div>}
            </div>
            {canEdit && (
              <div className="flex gap-2 mt-2">
                <Input placeholder={`Tambah item ${cl.label}…`} value={itemDrafts[cl.key] || ""} onChange={(e) => setItemDrafts((d) => ({ ...d, [cl.key]: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && addItem(cl.key)} className="text-sm" data-testid={`sw-${schema.kind}-${cl.key}-input`}/>
                <Button size="sm" onClick={() => addItem(cl.key)} className="bg-brand text-white" data-testid={`sw-${schema.kind}-${cl.key}-add`}><Plus className="w-4 h-4"/></Button>
              </div>
            )}
          </Card>
        );
      })}

      {/* Appointments */}
      {schema.showAppointments && (
        <Card className="p-4 border border-[#E5E0D8] bg-white space-y-3">
          <div className="text-sm font-semibold flex items-center gap-1"><Calendar className="w-4 h-4"/>{schema.appointmentLabel || "Jadwal"}</div>
          <div className="space-y-1.5">
            {(ws.appointments || []).map((a) => (
              <div key={a.id} className="flex items-start gap-2 group border border-[#F0EBE0] rounded px-2 py-1.5" data-testid={`sw-${schema.kind}-appt-${a.id}`}>
                <Checkbox checked={!!a.done} onCheckedChange={(v) => toggleAppointment(a.id, !!v)}/>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm ${a.done ? "line-through text-[#8C9096]" : ""}`}>{a.label}</div>
                  <div className="text-[11px] text-[#8C9096]">{formatDate(a.at)} • {a.location || "—"} {a.pic && `• PIC ${a.pic}`}</div>
                  {a.note && <div className="text-[11px] italic text-[#5C6066]">{a.note}</div>}
                </div>
                {canEdit && <button onClick={() => delAppointment(a.id)} className="opacity-0 group-hover:opacity-100 text-rose-500"><Trash2 className="w-3.5 h-3.5"/></button>}
              </div>
            ))}
            {(ws.appointments || []).length === 0 && <div className="text-xs italic text-[#8C9096]">Belum ada jadwal.</div>}
          </div>
          {canEdit && (
            <div className="grid grid-cols-5 gap-2 pt-2 border-t border-[#F0EBE0]">
              <Input placeholder="Label" value={ap.label} onChange={(e) => setAp({ ...ap, label: e.target.value })} className="text-sm" data-testid={`sw-${schema.kind}-appt-label`}/>
              <Input type="datetime-local" value={ap.at} onChange={(e) => setAp({ ...ap, at: e.target.value })} className="text-sm"/>
              <Input placeholder="Lokasi" value={ap.location} onChange={(e) => setAp({ ...ap, location: e.target.value })} className="text-sm"/>
              <Input placeholder="PIC" value={ap.pic} onChange={(e) => setAp({ ...ap, pic: e.target.value })} className="text-sm"/>
              <Button size="sm" onClick={addAppointment} className="bg-brand text-white" data-testid={`sw-${schema.kind}-appt-add`}><Plus className="w-4 h-4 mr-1"/>Tambah</Button>
            </div>
          )}
        </Card>
      )}

      {/* Handovers */}
      {schema.showHandovers && (
        <Card className="p-4 border border-[#E5E0D8] bg-white space-y-3">
          <div className="text-sm font-semibold flex items-center gap-1"><ArrowLeftRight className="w-4 h-4"/>Serah Terima</div>
          <div className="space-y-1.5">
            {(ws.handovers || []).map((h) => (
              <div key={h.id} className="flex items-start gap-2 group border border-[#F0EBE0] rounded px-2 py-1.5" data-testid={`sw-${schema.kind}-ho-${h.id}`}>
                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${h.kind === "out" ? "bg-sky-50 text-sky-700 border-sky-200" : h.kind === "in" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>{h.kind.toUpperCase()}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm">{schema.handoverLabels?.[h.kind] || h.kind}</div>
                  <div className="text-[11px] text-[#8C9096]">{formatDate(h.at)} • {h.by} {h.condition && `• kondisi: ${h.condition}`}</div>
                  {h.note && <div className="text-[11px] italic text-[#5C6066]">{h.note}</div>}
                  {(h.photos || []).map((p, idx) => (
                    <a key={idx} href={p} target="_blank" rel="noreferrer" className="text-[10px] text-brand hover:underline flex items-center gap-1"><ExternalLink className="w-3 h-3"/>{p.slice(0, 60)}</a>
                  ))}
                </div>
                {canEdit && <button onClick={() => delHandover(h.id)} className="opacity-0 group-hover:opacity-100 text-rose-500"><Trash2 className="w-3.5 h-3.5"/></button>}
              </div>
            ))}
            {(ws.handovers || []).length === 0 && <div className="text-xs italic text-[#8C9096]">Belum ada serah terima.</div>}
          </div>
          {canEdit && (
            <div className="grid grid-cols-4 gap-2 pt-2 border-t border-[#F0EBE0]">
              <Select value={ho.kind} onValueChange={(v) => setHo({ ...ho, kind: v })}>
                <SelectTrigger className="text-sm" data-testid={`sw-${schema.kind}-ho-kind`}><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="out">Keluar</SelectItem>
                  <SelectItem value="in">Masuk</SelectItem>
                  <SelectItem value="return">Kembali</SelectItem>
                </SelectContent>
              </Select>
              <Input placeholder="Kondisi (baik/lecet)" value={ho.condition} onChange={(e) => setHo({ ...ho, condition: e.target.value })} className="text-sm"/>
              <Input placeholder="Catatan" value={ho.note} onChange={(e) => setHo({ ...ho, note: e.target.value })} className="text-sm"/>
              <Button size="sm" onClick={addHandover} className="bg-brand text-white" data-testid={`sw-${schema.kind}-ho-add`}><Plus className="w-4 h-4 mr-1"/>Catat</Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
